import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PLAYERS } from "../lib/quattro";
import DoubleUpAdButton from "./DoubleUpAdButton";
import VictoryAnimation from "./VictoryAnimation";
import ShareWinButton from "./ShareWinButton";

const TOURNAMENT_BADGES = {
  freeroll:    { label: "DAILY FREEROLL CHAMPION", color: "#3DDC97", icon: "🎯", shareName: "Daily Freeroll" },
  daily_cup:   { label: "DAILY CUP CHAMPION",      color: "#FFD700", icon: "🏆", shareName: "Daily Cup" },
  weekly_crown:{ label: "WEEKLY CROWN HOLDER",     color: "#B537F2", icon: "👑", shareName: "Weekly Crown" },
  high_roller: { label: "HIGH ROLLER",             color: "#00C2FF", icon: "💎", shareName: "High Roller" },
  whale_table: { label: "WHALE TABLE",             color: "#FF3D6E", icon: "🐋", shareName: "Whale Table" },
};

// Confetti dots scattered behind the modal on a tournament win.
const ConfettiBurst = ({ color = "#FFD700" }) => {
  const dots = Array.from({ length: 18 });
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl">
      {dots.map((_, i) => {
        const angle = (i / dots.length) * Math.PI * 2;
        const dist = 120 + (i % 4) * 30;
        const x = Math.cos(angle) * dist;
        const y = Math.sin(angle) * dist;
        const palette = ["#FFD700", "#00E5FF", "#FF2A2A", "#B537F2", color];
        return (
          <motion.span
            key={i}
            initial={{ x: 0, y: 0, opacity: 0, scale: 0.4 }}
            animate={{ x, y, opacity: [0, 1, 1, 0], scale: 1 }}
            transition={{ duration: 1.6, delay: i * 0.025, ease: "easeOut" }}
            className="absolute left-1/2 top-1/2 rounded-sm"
            style={{
              width: 8, height: 12,
              background: palette[i % palette.length],
              translate: "-50% -50%",
              transform: `rotate(${i * 24}deg)`,
            }}
          />
        );
      })}
    </div>
  );
};

export default function ResultModal({
  open,
  winner,
  humanId,
  chipsDelta = 0,
  gemsDelta = 0,
  tournamentId = null,
  onPlayAgain,
  onLobby,
  onAdCredited,  // optional — when provided, shows "Watch ad +200" CTA on win
  onDoubleDownCredited, // optional — when provided + lost, shows "recover 25% via ad" CTA
  stake = 0,    // for double-down: the chips just lost (used to compute 25% recovery)
  victoryAnim = null, // user's active_victory_anim cosmetic (only fires on win)
}) {
  const won = winner === humanId;
  const draw = winner == null;
  // Defensive: winner is normally a seat index 0-3, but defensively coerce so
  // weird types (string "0", null, etc.) don't break `PLAYERS[winner].name`.
  const winnerIdx = typeof winner === "number" ? winner : Number.parseInt(winner, 10);
  const p = (winner != null && Number.isFinite(winnerIdx)) ? PLAYERS[winnerIdx] : null;
  const safeDelta = Number.isFinite(chipsDelta) ? chipsDelta : 0;
  const trophy = won && tournamentId ? TOURNAMENT_BADGES[tournamentId] : null;
  const [bonusChips, setBonusChips] = useState(0);
  const handleAdCredit = (chips) => {
    setBonusChips((b) => b + chips);
    onAdCredited?.(chips);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.78)" }}
          data-testid="result-modal"
        >
          {/* Victory animation overlay — only when the human player won */}
          {won && <VictoryAnimation variant={victoryAnim} />}
          <motion.div
            initial={{ scale: 0.85, y: 30 }} animate={{ scale: 1, y: 0 }}
            className="glass max-w-md w-full p-8 rounded-3xl text-center relative overflow-hidden"
            style={{
              maxHeight: "92vh",
              overflowY: "auto",
              borderColor: trophy ? trophy.color : (p ? p.color : undefined),
              boxShadow: trophy ? `0 0 60px ${trophy.color}55, inset 0 0 32px ${trophy.color}22` : undefined,
            }}
            data-testid="result-card"
          >
            {trophy && <ConfettiBurst color={trophy.color} />}

            {trophy ? (
              <>
                <div className="relative">
                  <motion.div
                    initial={{ scale: 0, rotate: -20 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 220, damping: 14, delay: 0.1 }}
                    style={{ fontSize: 64 }}
                  >
                    {trophy.icon}
                  </motion.div>
                  <div
                    className="label-eyebrow mt-2 mb-1"
                    style={{ color: trophy.color, letterSpacing: 3, fontSize: 11 }}
                    data-testid="trophy-badge"
                  >{trophy.label}</div>
                  <h1
                    className="font-display font-black text-5xl mb-2"
                    style={{ color: trophy.color, textShadow: `0 0 30px ${trophy.color}66` }}
                  >Champion!</h1>
                </div>

                <div className="flex items-center justify-center gap-4 mb-2 mt-4">
                  <div className="flex items-center gap-1.5">
                    <span style={{ color: "#FFD700", fontSize: 22 }}>◆</span>
                    <span
                      className="font-mono font-bold text-2xl"
                      style={{ color: "#FFD700" }}
                      data-testid="chips-delta"
                    >+{safeDelta}</span>
                  </div>
                  {gemsDelta > 0 && (
                    <motion.div
                      initial={{ scale: 0.6, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.4, type: "spring", stiffness: 260 }}
                      className="flex items-center gap-1.5 rounded-full px-3 py-1"
                      style={{
                        background: "rgba(0,229,255,0.10)",
                        border: "1px solid rgba(0,229,255,0.5)",
                        boxShadow: "0 0 18px rgba(0,229,255,0.35)",
                      }}
                      data-testid="gems-delta"
                    >
                      <span style={{ color: "#00E5FF", fontSize: 18 }}>◈</span>
                      <span className="font-mono font-bold text-xl" style={{ color: "#00E5FF" }}>
                        +{gemsDelta}
                      </span>
                    </motion.div>
                  )}
                </div>
                <p className="text-white/55 text-xs mb-6">
                  {gemsDelta > 0 ? "Pot + tournament gem bonus credited." : "Pot credited."}
                </p>
              </>
            ) : (
              <>
                <div className="label-eyebrow mb-2">{draw ? "Game Over" : won ? "Victory" : "Defeat"}</div>
                <h1 className="font-display font-black text-5xl mb-2" style={{ color: p ? p.color : "#fff" }}>
                  {draw ? "Draw" : won ? "You Win!" : `${p?.name || "Opponent"} Wins`}
                </h1>
                <p className="text-white/70 mb-6">
                  {draw ? "Decks emptied without a 4-in-a-row." :
                    won ? "Four in a row! Glorious." : "Four in a row achieved by an opponent."}
                </p>
                <div className="flex items-center justify-center gap-2 mb-6">
                  <span className="label-eyebrow">Chips</span>
                  <span className="font-mono font-bold text-2xl" style={{ color: safeDelta >= 0 ? "#FFD700" : "#FF6B6B" }} data-testid="chips-delta">
                    {safeDelta >= 0 ? `+${safeDelta}` : safeDelta}
                  </span>
                </div>
              </>
            )}

            <div className="flex gap-3 justify-center relative">
              <button className="btn-primary" onClick={onPlayAgain} data-testid="play-again-btn">Play Again</button>
              <button className="btn-ghost" onClick={onLobby} data-testid="back-lobby-btn">Lobby</button>
            </div>

            {/* Share-win CTA — only on a human win. The victory moment is the
                #1 emotional peak for converting a friend invite. Both the
                inviter and invitee get +500 chips when the friend redeems. */}
            {won && (
              <ShareWinButton tournamentName={trophy ? trophy.shareName : null} />
            )}

            {/* Rewarded-ad CTA — only on solo wins, only when callback provided */}
            {won && onAdCredited && (
              <div className="mt-5">
                {bonusChips > 0 ? (
                  <div
                    className="text-sm font-display font-bold"
                    style={{ color: "#3DDC97" }}
                    data-testid="ad-bonus-credited"
                  >+{bonusChips} bonus chips credited!</div>
                ) : (
                  <DoubleUpAdButton
                    onCredited={handleAdCredit}
                    label="Watch ad · +200 bonus chips"
                  />
                )}
              </div>
            )}

            {/* Double-Down recovery CTA — only on losses, when callback + stake provided.
                Recovers 25% of the stake (capped at 250 chips) via a rewarded ad.
                Helps cushion the sting of a loss and keeps the player engaged. */}
            {!won && !draw && onDoubleDownCredited && stake > 0 && (
              <div className="mt-5">
                <DoubleUpAdButton
                  onCredited={(chips) => {
                    onDoubleDownCredited(chips);
                  }}
                  label={`Watch ad · recover ~${Math.min(Math.round(stake * 0.25), 250)} chips`}
                  rewardId="chips_double_down"
                  stake={stake}
                />
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
